import { useEffect, useState } from "react";
import { When } from "react-if";
import { useDispatch } from "react-redux";
import {
  IMobileNumberFixApplyResponse,
  IMobileNumberFixProposal,
  IMobileNumberFixResult,
  MobileNumberCheckStatus,
  MOBILE_NUMBER_FIX_MAX_CONTACTS,
  mobileNumberFixScope,
} from "shared";

import { Button } from "../../../common/Atoms/Button";
import { CheckBox } from "../../../common/Atoms/CheckBox";
import { Modal } from "../../../common/Atoms/Modal";
import { Notification } from "../../../common/Atoms/Notification";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useModal } from "../../hooks/useModals";
import {
  useApplyMobileNumberFixMutation,
  useFetchMobileNumberFixQuery,
  useFixContactMobileNumbersMutation,
  useStartMobileNumberFixMutation,
} from "../../services/api/mobileNumberCheckApi/mobileNumberCheck";
import { open as openAssistant } from "../../slices/assistantDrawer";

const IN_PROGRESS_POLL_MS = 2_000;

interface IMobileNumberFixModalContentProps {
  open: boolean;
  hide: () => void;
  /** Fix just this contact: runs as soon as the modal opens, with no background task */
  contactId?: number;
  /** Fix just the contacts selected in the contact table. The modal first lists the ones that will be checked */
  selection?: IMobileNumberFixSelection[];
}

export interface IMobileNumberFixSelection {
  id: number;
  name: string;
  hasInvalidMobile: boolean;
}

/** Org wide AI fix. Mounted once in Root, opened via the `mobileNumberFix` modal state */
export function MobileNumberFixModal() {
  const { state: open, hide } = useModal(`mobileNumberFix`);

  return (
    <MobileNumberFixModalContent
      open={ open }
      hide={ hide }
    />
  );
}

/** AI suggested corrections for invalid mobile numbers, listed per contact for the user to approve in bulk */
export function MobileNumberFixModalContent({ open, hide, contactId, selection }: IMobileNumberFixModalContentProps) {
  const currentOrg = useGetSelectedOrganisation();
  const dispatch = useDispatch();
  const missingConsent = !!currentOrg && currentOrg.aiConsentGiven !== true;
  const [pollingInterval, setPollingInterval] = useState(0);
  // `currentData` so another org's run is never shown after switching org
  const { currentData } = useFetchMobileNumberFixQuery(currentOrg?.id, {
    skip: !currentOrg || !open || !!contactId,
    pollingInterval,
  });
  const [startFix, { isLoading: starting }] = useStartMobileNumberFixMutation();
  const [fixContact, { isLoading: fixingContact, isError: fixContactFailed }] = useFixContactMobileNumbersMutation();
  const [applyFix, { isLoading: applying }] = useApplyMobileNumberFixMutation();
  const [contactResult, setContactResult] = useState<IMobileNumberFixResult | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [applied, setApplied] = useState<Pick<IMobileNumberFixApplyResponse, `appliedCount` | `failed`> | null>(null);

  const eligible = (selection || []).filter(contact => contact.hasInvalidMobile);
  // Whole org when there is no selection. The org has one task and one stored result, which may be for another scope
  // (a selection with nothing to check never matches a whole org run either)
  const scope = selection && !eligible.length ? `none` : mobileNumberFixScope(selection && eligible.map(contact => contact.id));
  const check = contactId ? null : currentData?.check || null;
  const otherScopeRunning = !!check && check.status !== MobileNumberCheckStatus.FAILED && (check.scope || ``) !== scope;
  const ownCheck = check && (check.scope || ``) === scope ? check : null;
  const failedCheck = ownCheck?.status === MobileNumberCheckStatus.FAILED ? ownCheck : null;
  const runningCheck = ownCheck && !failedCheck ? ownCheck : null;
  // A selection always opens on the list of contacts it will check, its result only shows once run from here
  const [ranScope, setRanScope] = useState<string | null>(null);
  const storedResult = currentData?.result && (currentData.result.scope || ``) === scope ? currentData.result : null;
  const orgResult = !selection || ranScope === scope ? storedResult : null;
  const result = contactId ? contactResult : orgResult || null;
  // A result cached before reasons were added has no list
  const unfixable = result?.unfixable || [];

  // Proposals grouped per contact: a contact is approved as a whole, it is one update to the accounting system
  const contacts = Object.values((result?.proposals || []).reduce<Record<number, IMobileNumberFixProposal[]>>((groups, proposal) => {
    (groups[proposal.contactId] ||= []).push(proposal);

    return groups;
  }, {}));
  const selected = contacts.filter(proposals => selectedContactIds.includes(proposals[0].contactId));
  const atLimit = selected.length >= MOBILE_NUMBER_FIX_MAX_CONTACTS;

  useEffect(() => {
    setPollingInterval(runningCheck || otherScopeRunning ? IN_PROGRESS_POLL_MS : 0);
  }, [!!runningCheck, otherScopeRunning]);

  async function runContactFix() {
    const response = await fixContact({ organisationId: currentOrg.id, contactId });

    if (`data` in response) {
      setContactResult(response.data.result);
      // Nothing to choose between for a single contact
      setSelectedContactIds([contactId]);
    }
  }

  function run() {
    setApplied(null);
    setSelectedContactIds([]);

    if (contactId) {
      runContactFix();
    }
    else {
      setRanScope(scope);
      startFix({ organisationId: currentOrg.id, contactIds: selection ? eligible.map(contact => contact.id) : undefined });
    }
  }

  useEffect(() => {
    if (open && contactId && currentOrg && !missingConsent) {
      setContactResult(null);
      run();
    }
  }, [open, contactId]);

  async function onApply() {
    const response = await applyFix({
      organisationId: currentOrg.id,
      contactPersonIds: selected.flat().map(proposal => proposal.contactPersonId),
      contactId,
    });

    if (`data` in response) {
      setApplied(response.data);
      setSelectedContactIds([]);

      if (contactId) {
        setContactResult(response.data.result);
      }
    }
  }

  function renderProposals() {
    return (
      <>
        <Paragraph>
          { `${contacts.length} ${contacts.length === 1 ? `contact has` : `contacts have`} a suggested fix. ` }
          { !contactId && `Approve up to ${MOBILE_NUMBER_FIX_MAX_CONTACTS} contacts at a time, each one is updated in your accounting system.` }
        </Paragraph>

        <div className={ `mt-3 max-h-96 overflow-y-auto divide-y divide-gray-100 rounded-md border border-gray-200` }>
          { contacts.map(proposals => {
            const id = proposals[0].contactId;
            const checked = selectedContactIds.includes(id);

            return (
              <div
                key={ id }
                className={ `flex items-start gap-3 px-3 py-2` }
              >
                <CheckBox
                  name={ `fix_${id}` }
                  checked={ checked }
                  disabled={ applying || (!checked && atLimit) }
                  onChange={ ({ value }) => setSelectedContactIds(ids => (value ? [...ids, id] : ids.filter(item => item !== id))) }
                />
                <div className={ `min-w-0 text-sm` }>
                  <div className={ `font-medium text-gray-900` }>{ proposals[0].contactName }</div>
                  { proposals.map(proposal => (
                    <div
                      key={ proposal.contactPersonId }
                      className={ `text-gray-500` }
                    >
                      { proposal.personName && `${proposal.personName}: ` }
                      <span className={ `text-red-700 line-through` }>{ proposal.currentNumber }</span>
                      { ` → ` }
                      <span className={ `font-medium text-paidniceGreen-700` }>{ proposal.proposedNumber }</span>
                    </div>
                  )) }
                </div>
              </div>
            );
          }) }
        </div>
      </>
    );
  }

  function renderBody() {
    if (missingConsent) {
      return (
        <Notification type={ `warning` }>
          { `Contact details are sent to our AI provider to work out the right format, and your organisation hasn't agreed to AI data use yet. Give consent in the AI assistant, then come back here.` }
        </Notification>
      );
    }

    if (otherScopeRunning) {
      return (
        <Notification type={ `warning` }>
          { `Another AI mobile number fix is already running for this organisation (${check?.progressMessage}). This one can be started once it has finished.` }
        </Notification>
      );
    }

    if (runningCheck || fixingContact) {
      const progress = runningCheck?.progress || 0;

      return (
        <>
          <Paragraph>{ runningCheck?.progressMessage || `Working out the right format...` }</Paragraph>
          <When condition={ !!runningCheck }>
            <div
              className={ `mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200` }
              role={ `progressbar` }
              aria-valuemin={ 0 }
              aria-valuemax={ 100 }
              aria-valuenow={ Math.round(progress * 100) }
            >
              <div
                className={ `h-full rounded-full bg-paidniceGreen-600 transition-all duration-500` }
                style={ { width: `${progress * 100}%` } }
              />
            </div>
            <Paragraph
              variant={ `secondary` }
              className={ `mt-3` }
            >
              { `You can close this window, it carries on in the background. Reopen it to see the suggestions.` }
            </Paragraph>
          </When>
        </>
      );
    }

    if (failedCheck || fixContactFailed) {
      return (
        <Notification type={ `error` }>
          { failedCheck?.progressMessage || `The AI fix failed, please try again` }
        </Notification>
      );
    }

    if (result) {
      return (
        <>
          { applied && (
            <Notification
              type={ applied.failed.length ? `warning` : `success` }
              className={ `mb-3` }
            >
              { `Updated ${applied.appliedCount} mobile ${applied.appliedCount === 1 ? `number` : `numbers`}.` }
              { applied.failed.map(failure => ` ${failure.contactName} could not be updated: ${failure.error}.`).join(``) }
            </Notification>
          ) }
          { contacts.length ? renderProposals() : <Paragraph>{ applied ? `No more suggested fixes.` : `No mobile numbers could be fixed.` }</Paragraph> }
          { !!unfixable.length && (
            <>
              <Paragraph
                variant={ `secondary` }
                className={ `mt-3` }
              >
                { `${unfixable.length} invalid mobile ${unfixable.length === 1 ? `number` : `numbers`} couldn't be fixed confidently and ${unfixable.length === 1 ? `needs` : `need`} correcting by hand:` }
              </Paragraph>
              <div className={ `mt-2 max-h-60 overflow-y-auto divide-y divide-gray-100 rounded-md border border-gray-200` }>
                { unfixable.map(item => (
                  <div
                    key={ item.contactPersonId }
                    className={ `px-3 py-2 text-sm` }
                  >
                    <div className={ `font-medium text-gray-900` }>
                      { [item.contactName, item.personName].filter(Boolean).join(` · `) }
                      <span className={ `ml-2 font-normal text-red-700` }>{ item.currentNumber }</span>
                    </div>
                    <div className={ `text-gray-500` }>{ item.reason }</div>
                  </div>
                )) }
              </div>
            </>
          ) }
        </>
      );
    }

    if (selection) {
      return eligible.length ? (
        <>
          <Paragraph>
            { `${eligible.length} of the ${selection.length} selected ${selection.length === 1 ? `contact has` : `contacts have`} an invalid mobile number and will be checked. Nothing is changed until you approve it.` }
          </Paragraph>
          <ul className={ `mt-3 max-h-60 overflow-y-auto list-disc pl-8 pr-3 py-2 text-sm text-gray-700 rounded-md border border-gray-200` }>
            { eligible.map(contact => <li key={ contact.id }>{ contact.name }</li>) }
          </ul>
        </>
      ) : (
        <Paragraph>
          { `None of the ${selection.length === 1 ? `selected contact's mobile numbers are` : `${selection.length} selected contacts have a mobile number`} flagged as invalid, so there is nothing to fix.` }
        </Paragraph>
      );
    }

    return (
      <Paragraph>
        { `AI looks at every mobile number flagged as invalid and suggests the correct international format, using the contact's address and currency and your organisation's region. Nothing is changed until you approve it.` }
      </Paragraph>
    );
  }

  const idle = !missingConsent && !runningCheck && !fixingContact && !otherScopeRunning;

  return (
    <Modal
      open={ open }
      setOpen={ hide }
      title={ `Fix mobile numbers with AI` }
    >
      { renderBody() }

      <div className={ `flex justify-end space-x-2 mt-4` }>
        <Button
          onClick={ hide }
          color={ `white` }
        >
          { `Close` }
        </Button>
        { missingConsent && (
          <Button
            onClick={ () => {
              hide();
              dispatch(openAssistant());
            } }
          >
            { `Open AI assistant` }
          </Button>
        ) }
        { idle && (
          <Button
            onClick={ run }
            color={ contacts.length ? `white` : undefined }
            loading={ starting }
            disabled={ starting || applying || (!!selection && !eligible.length) }
          >
            { result || failedCheck || fixContactFailed ? `Run again` : `Find fixes` }
          </Button>
        ) }
        { idle && !contactId && contacts.length > MOBILE_NUMBER_FIX_MAX_CONTACTS && (
          <Button
            onClick={ () => setSelectedContactIds(contacts.slice(0, MOBILE_NUMBER_FIX_MAX_CONTACTS).map(proposals => proposals[0].contactId)) }
            color={ `white` }
            disabled={ applying }
          >
            { `Select first ${MOBILE_NUMBER_FIX_MAX_CONTACTS}` }
          </Button>
        ) }
        { idle && !!contacts.length && (
          <Button
            onClick={ onApply }
            loading={ applying }
            disabled={ applying || !selected.length }
          >
            { `Approve ${selected.length || ``} ${selected.length === 1 ? `contact` : `contacts`}`.replace(/\s+/g, ` `) }
          </Button>
        ) }
      </div>
    </Modal>
  );
}
